import { memo, useCallback, useEffect, useMemo, useState } from "react";

import { BusinessFilter } from "../../../../components/shared/main-filters/business";
import {DropdownOptionModel, DropdownOptionValue} from "../../../../components/shared/dropdown/models";
import { Button, Dropdown, InputsSection } from "../../../../components/shared";
import { betShops } from "../../../../../models/api/games-control";
import { GameGroupsList } from "../../../../../enums/game-groups";
import { dataToDropDownConverter } from "../../../../../helpers";
import TransferListSection from "../../transfer-list-section";
import { useTranslation } from "../../../../../hooks";
import { GameType } from "../../../../../models";
import { T } from "../../../../../constants";
import { Icons } from "../../../../../enums";

interface EditGameControlFormProps {
  values: any;
  partnerId?: number;
  hideFilter: boolean;
  gameTypes: Array<GameType>;
  resetBetShopList: () => void;
  betShopsItems: Array<betShops>;
  setBetShopsItems: (data: any) => void;
  bettingTimeRtpDropdownData: Array<DropdownOptionModel>;
  handleDropdownChange: (value: DropdownOptionValue, key: string) => void;
}

const EditGameControlForm: React.FC<EditGameControlFormProps> = ({
  values,
  partnerId,
  gameTypes,
  hideFilter,
  betShopsItems,
  setBetShopsItems,
  resetBetShopList,
  handleDropdownChange,
  bettingTimeRtpDropdownData,
}) => {
  const t = useTranslation();

  const [activeBetShopSelectedData, setActiveBetShopSelectedData] = useState<{ [key: number]: boolean}>({});

  const [inactiveBetShopSelectedData, setInactiveBetShopSelectedData] = useState<{[key: number]: boolean}>({});

  const [activeBetShops, setActiveBetShops] = useState<betShops[]>([]);
  const [inActiveBetShops, setInActiveBetShops] = useState<betShops[]>([]);

  useEffect(() => {
    return () => {
      resetBetShopList();
    };
  }, []);

  useEffect(() => {
    const data = betShopsItems.reduce<{
      activeBetShops: betShops[];
      inActiveBetShops: betShops[];
    }>(
      (acc, currentData) => {
        acc[currentData.isActive ? "activeBetShops" : "inActiveBetShops"].push(
          currentData,
        );

        return acc;
      },
      { inActiveBetShops: [], activeBetShops: [] },
    );

    setActiveBetShops(data.activeBetShops);
    setInActiveBetShops(data.inActiveBetShops);
  }, [betShopsItems]);

  const transferActiveBetShops = useCallback(() => {
    if (!Object.keys(activeBetShopSelectedData).length) return;

    const currentData = [...betShopsItems];
    setBetShopsItems(
      currentData.map((item) => ({
        ...item,
        gameTypeId: values.gameTypeId,
        isActive: activeBetShopSelectedData[item.betshopId]
          ? false
          : item.isActive,
      })),
    );

    setActiveBetShopSelectedData({});
  }, [activeBetShops, inActiveBetShops, activeBetShopSelectedData, values]);

  const transferInActiveBetShops = useCallback(() => {
    if (!Object.keys(inactiveBetShopSelectedData).length) return;

    const currentData = [...betShopsItems];
    setBetShopsItems(
      currentData.map((item) => ({
        ...item,
        isActive: inactiveBetShopSelectedData[item.betshopId] || item.isActive,
      })),
    );
    setInactiveBetShopSelectedData({});
  }, [activeBetShops, inActiveBetShops, inactiveBetShopSelectedData]);

  const gameGroupData = useMemo(() => {
    return dataToDropDownConverter(GameGroupsList, {
      labelKey: "name",
      valueKey: "id",
    });
  }, []);

  return (
    <div className="page-info-holder">
      <div className="game-control">
        <div className="game-control-inner">
          {!hideFilter ? (
            <InputsSection title={T.GAMES_CONTROL}>
              <Dropdown
                withSearch
                data={gameGroupData}
                value={`${values.gameType}`}
                label={t(T.CHOOSE_GAME_GROUP)}
                onChange={(value) => handleDropdownChange(value as DropdownOptionValue, "gameType")}
              />
            </InputsSection>
          ) : null}
            <InputsSection title={T.BETSHOP}>
              {
                (!hideFilter && partnerId) && (
                  <BusinessFilter
                    hasAll={false}
                    partnerId={partnerId}
                    value={values.businessUnitId}
                    onChange={(value) =>
                      handleDropdownChange(
                        value as DropdownOptionValue,
                        "businessUnitId",
                      )
                    }
                  />
                )
              }
              <Dropdown
                withSearch
                label={t(T.BETTING_TIME_RTP)}
                value={`${values.gameTypeId}`}
                data={bettingTimeRtpDropdownData}
                onChange={(value) => handleDropdownChange(value as DropdownOptionValue, "gameTypeId")}
              />
            </InputsSection>
        </div>

        <div className="transfer-wrapper">
          <TransferListSection
            list={activeBetShops}
            title={T.ACTIVE_BET_SHOPS}
            selectedItems={activeBetShopSelectedData}
            onChange={setActiveBetShopSelectedData}
          />
          {/*TODO create component transferredAction*/}
          <div className="transfer-operation">
            <Button
              icon={Icons.ArrowLeft}
              className="transfer-btn"
              appearance="none"
              onClick={transferInActiveBetShops}
            />
            <Button
              icon={Icons.ArrowRight}
              className="transfer-btn"
              appearance="none"
              onClick={transferActiveBetShops}
            />
          </div>

          <div className="transfer-list">
            <TransferListSection
              list={inActiveBetShops}
              title={T.INACTIVE_BET_SHOPS}
              selectedItems={inactiveBetShopSelectedData}
              onChange={setInactiveBetShopSelectedData}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(EditGameControlForm);
